/**
 * Demo Mode
 *
 * When an admin enables `demo_mode` on a user account, the supervision
 * console shows realistic pre-seeded live calls and supervisor shift data.
 *
 * Off by default for all accounts.
 */

import { useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { getBooleanSetting } from "./userAccess";

export const DEMO_MODE_STORAGE_KEY = "techladder_demo_mode";

/**
 * Returns true if demo mode is active synchronously (usable outside React hooks,
 * such as in API client wrappers and data loaders).
 */
export function isDemoModeActive(): boolean {
  if (typeof window === "undefined" || !window.localStorage) return false;
  try {
    return localStorage.getItem(DEMO_MODE_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

/**
 * Hook to access demo mode inside React components.
 * Returns true when `demo_mode` is enabled for the signed-in user.
 */
export function useDemoMode(): boolean {
  let user: any = null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const auth = useAuth();
    user = auth?.user;
  } catch {
    // Graceful fallback outside AuthProvider (e.g. isolated unit tests)
  }
  const active = getBooleanSetting(user, "demo_mode", false) || isDemoModeActive();

  useEffect(() => {
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        if (getBooleanSetting(user, "demo_mode", false)) {
          localStorage.setItem(DEMO_MODE_STORAGE_KEY, "true");
        }
      } catch {}
    }
  }, [user]);

  return active;
}
